/**
 * Contractor Command Center (P8) — pure derivations.
 *
 * Read-only consumer of:
 *   • Job assignments (via assignedSubcontractor / assignedSubcontractorId)
 *   • MarketplaceOffer (P1) — pending offers + payoutAmountSnapshot
 *   • getVisibleOpportunities (P3) — opportunity exposure
 *
 * Hard rules (locked):
 *   • No mutations.
 *   • No customer-revenue read or display.
 *   • No availability writes / scheduling engine.
 *   • Pending offers excluded from earnings.
 *   • Realized earnings only from completed jobs (getJobCompletionDate).
 *   • Tentative earnings = accepted/assigned, not-yet-completed.
 */

import type { Job } from "@/data/types";
import type { MarketplaceOffer } from "./marketplaceOffer";

/**
 * Resolve a job's completion date for earnings classification.
 *
 * Priority (locked):
 *   1. (job as any).installCompletedAt
 *   2. (job as any).completedAt
 *   3. wrap-confirmed activity timestamp (action contains "wrap" or
 *      "completed" with noteType "subcontractor"/"crew")
 *   4. Otherwise null
 *
 * Note: kept local to P8. The canonical Job type does not carry an explicit
 * completion timestamp yet; this helper tolerates that and falls back to
 * activity history. `currentStage === "Completed"` alone is NOT enough
 * because it has no timestamp and could be retro-stamped.
 */
export function getJobCompletionDate(job: Job): string | null {
  // Loose typed lookups — these fields may exist on extended job records.
  const j = job as Job & {
    installCompletedAt?: string;
    completedAt?: string;
  };
  if (j.installCompletedAt) return j.installCompletedAt;
  if (j.completedAt) return j.completedAt;

  // Activity-history fallback: most recent wrap/completed entry.
  const wrap = [...(job.activityHistory ?? [])]
    .reverse()
    .find((a) => {
      const action = (a.action || "").toLowerCase();
      return (
        action.includes("wrap") ||
        action.includes("install completed") ||
        action.includes("job completed")
      );
    });
  return wrap?.date ?? null;
}

/* ── Date helpers (local, no engine dependency) ───────────────────── */

function toDateOnly(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

function isToday(dateOnly: string, now: Date): boolean {
  return dateOnly === now.toISOString().slice(0, 10);
}

function isInThisWeek(dateOnly: string, now: Date): boolean {
  // Week = Monday..Sunday containing `now`.
  const d = new Date(dateOnly + "T00:00:00");
  const today = new Date(now.toISOString().slice(0, 10) + "T00:00:00");
  const dayOfWeek = (today.getUTCDay() + 6) % 7; // Mon = 0
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() - dayOfWeek);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return d >= monday && d <= sunday;
}

/* ── Filtering ────────────────────────────────────────────────────── */

export function filterContractorJobs(
  jobs: Job[],
  contractorId: string,
  matchKey: string,
): Job[] {
  if (!contractorId && !matchKey) return [];
  return jobs.filter(
    (j) =>
      j.assignedSubcontractorId === contractorId ||
      j.assignedSubcontractor === matchKey ||
      j.assignedCrew === matchKey,
  );
}

export function filterContractorPendingOffers(
  offers: MarketplaceOffer[],
  contractorId: string,
  now: Date = new Date(),
): MarketplaceOffer[] {
  const nowMs = now.getTime();
  return offers.filter(
    (o) =>
      o.contractorId === contractorId &&
      o.status === "pending" &&
      new Date(o.expiresAt).getTime() > nowMs,
  );
}

/* ── Calendar item shape ──────────────────────────────────────────── */

export type CommandCenterItemKind =
  | "assigned_scheduled"
  | "assigned_unscheduled"
  | "pending_offer"
  | "opportunity";

export interface CommandCenterItem {
  kind: CommandCenterItemKind;
  jobId: string;
  /** Human-readable job reference (jobNumber or short ID fallback). */
  jobLabel: string;
  date: string | null; // YYYY-MM-DD or null when unscheduled
  title: string;
  city?: string;
  payoutAmount: number | null;
  /** For pending offers: ms remaining until expiry. */
  expiresInMs?: number;
  /** Pre-resolved status label for read-only display. */
  statusLabel: string;
}

/* ── Earnings ─────────────────────────────────────────────────────── */

export interface ContractorEarnings {
  /** Realized: completed jobs in window, payout snapshot. */
  todayRealized: number;
  weekRealized: number;
  /** Tentative: accepted/assigned, not-yet-completed. */
  upcomingTentative: number;
  /** Counts for transparency in tests / UI. */
  counts: {
    realizedToday: number;
    realizedWeek: number;
    tentative: number;
  };
}

/**
 * Resolve the contractor-pay snapshot for a job.
 *
 * Priority:
 *   1. Most recent ACCEPTED offer's `payoutAmountSnapshot` for this job
 *      (the offer carries the pay snapshot at acceptance time).
 *   2. `job.payoutAmount` (contractor-pay field).
 *
 * Never reads any customer-revenue field.
 */
export function resolveJobPayoutSnapshot(
  job: Job,
  offers: MarketplaceOffer[],
): number {
  const accepted = offers
    .filter((o) => o.jobId === job.id && o.status === "accepted")
    .sort((a, b) => (b.respondedAt ?? "").localeCompare(a.respondedAt ?? ""))[0];
  if (accepted?.payoutAmountSnapshot != null) {
    return accepted.payoutAmountSnapshot;
  }
  return job.payoutAmount ?? 0;
}

export function computeContractorEarnings(
  jobs: Job[],
  offers: MarketplaceOffer[],
  now: Date = new Date(),
): ContractorEarnings {
  let todayRealized = 0;
  let weekRealized = 0;
  let upcomingTentative = 0;
  let realizedToday = 0;
  let realizedWeek = 0;
  let tentative = 0;

  for (const job of jobs) {
    const completion = getJobCompletionDate(job);
    const payout = resolveJobPayoutSnapshot(job, offers);

    if (completion) {
      const d = toDateOnly(completion);
      if (isInThisWeek(d, now)) {
        weekRealized += payout;
        realizedWeek++;
      }
      if (isToday(d, now)) {
        todayRealized += payout;
        realizedToday++;
      }
    } else {
      // Tentative — accepted/assigned, not yet completed.
      upcomingTentative += payout;
      tentative++;
    }
  }

  return {
    todayRealized,
    weekRealized,
    upcomingTentative,
    counts: { realizedToday, realizedWeek, tentative },
  };
}

/* ── Calendar derivation ──────────────────────────────────────────── */

export function deriveCommandCenterCalendar(args: {
  jobs: Job[];
  offers: MarketplaceOffer[];
  visibleOpportunityJobIds: string[];
  jobLookup: Map<string, Job>;
  now?: Date;
}): CommandCenterItem[] {
  const { jobs, offers, visibleOpportunityJobIds, jobLookup, now = new Date() } = args;
  const items: CommandCenterItem[] = [];

  for (const job of jobs) {
    if (getJobCompletionDate(job)) continue; // completed → not on the forward calendar
    const payout = resolveJobPayoutSnapshot(job, offers);
    if (job.scheduledInstallDate) {
      items.push({
        kind: "assigned_scheduled",
        jobId: job.id,
        jobLabel: String(job.jobNumber || job.id.slice(0, 8)),
        date: job.scheduledInstallDate,
        title: job.customerName,
        city: job.address?.city,
        payoutAmount: payout,
        statusLabel:
          job.scheduledInstallDate <= now.toISOString().slice(0, 10)
            ? "Scheduled"
            : "Upcoming",
      });
    } else {
      items.push({
        kind: "assigned_unscheduled",
        jobId: job.id,
        jobLabel: String(job.jobNumber || job.id.slice(0, 8)),
        date: null,
        title: job.customerName,
        city: job.address?.city,
        payoutAmount: payout,
        statusLabel: "Schedule needed",
      });
    }
  }

  for (const offer of filterContractorPendingOffers(offers, offers[0]?.contractorId ?? "", now)) {
    // (Caller passes a pre-filtered offers list — this loop is a no-op safety
    // when offers already contain only this contractor's pending offers.)
    void offer;
  }

  return items;
}

/**
 * Build pending-offer calendar items for a single contractor.
 */
export function buildPendingOfferItems(
  offers: MarketplaceOffer[],
  jobLookup: Map<string, Job>,
  now: Date = new Date(),
): CommandCenterItem[] {
  return offers.map((o) => {
    const job = jobLookup.get(o.jobId);
    return {
      kind: "pending_offer" as const,
      jobId: o.jobId,
      jobLabel: String(job?.jobNumber || o.jobId.slice(0, 8)),
      date: job?.scheduledInstallDate ?? null,
      title: job?.customerName ?? o.jobId.slice(0, 8),
      city: job?.address?.city,
      payoutAmount: o.payoutAmountSnapshot ?? null,
      expiresInMs: Math.max(0, new Date(o.expiresAt).getTime() - now.getTime()),
      statusLabel: "Pending response",
    };
  });
}

export function buildOpportunityItems(
  visibleOpportunityJobIds: string[],
  jobLookup: Map<string, Job>,
): CommandCenterItem[] {
  return visibleOpportunityJobIds
    .map((id) => jobLookup.get(id))
    .filter((j): j is Job => Boolean(j))
    .map((job) => ({
      kind: "opportunity" as const,
      jobId: job.id,
      jobLabel: String(job.jobNumber || job.id.slice(0, 8)),
      date: job.scheduledInstallDate || null,
      title: job.customerName,
      city: job.address?.city,
      // Opportunities surface payout (contractor-pay) only — never customer revenue.
      payoutAmount: job.payoutAmount ?? null,
      statusLabel: "Opportunity",
    }));
}

/* ── Inline notification banners (passive) ────────────────────────── */

export interface CommandCenterNotice {
  id: string;
  tone: "warning" | "info";
  message: string;
}

export function deriveInlineNotices(args: {
  pendingOffers: MarketplaceOffer[];
  assignedJobs: Job[];
  now?: Date;
}): CommandCenterNotice[] {
  const { pendingOffers, assignedJobs, now = new Date() } = args;
  const notices: CommandCenterNotice[] = [];
  const todayIso = now.toISOString().slice(0, 10);
  const tomorrowIso = new Date(now.getTime() + 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  // Expiring soon — under 30 minutes.
  for (const o of pendingOffers) {
    const ms = new Date(o.expiresAt).getTime() - now.getTime();
    if (ms > 0 && ms < 30 * 60 * 1000) {
      notices.push({
        id: `exp-${o.id}`,
        tone: "warning",
        message: `Offer expires soon — respond before it lapses.`,
      });
    }
  }

  // Job tomorrow.
  for (const j of assignedJobs) {
    if (getJobCompletionDate(j)) continue;
    if (j.scheduledInstallDate === tomorrowIso) {
      notices.push({
        id: `tom-${j.id}`,
        tone: "info",
        message: `Job ${j.jobNumber || j.id.slice(0, 8)} is scheduled tomorrow.`,
      });
    }
  }

  // Unscheduled work.
  const unscheduled = assignedJobs.filter(
    (j) => !getJobCompletionDate(j) && !j.scheduledInstallDate,
  );
  if (unscheduled.length > 0) {
    notices.push({
      id: "unscheduled",
      tone: "warning",
      message: `You have ${unscheduled.length} accepted job${unscheduled.length === 1 ? "" : "s"} that still ${unscheduled.length === 1 ? "needs" : "need"} scheduling.`,
    });
  }

  void todayIso;
  return notices;
}
